import { type IListDevicesHandler, ListDevices, ListedDevice } from '@telespot/management/use-cases';
import { ParseBaseRepository } from '../../parse-base.repository';
import { ObjectTopology } from '../../parse.topology';
import { DeviceTopology, ParseDeviceMapper } from '../../repositories/acquisition/parse-device.mapper';
import {
  OrganizationTopology,
  ParseOrganizationMapper,
} from '../../repositories/organization/parse-organization.mapper';

/**
 * @group Devices
 * @category Handlers
 */
export class ListDevicesHandler extends ParseBaseRepository implements IListDevicesHandler {
  private readonly deviceMapper = new ParseDeviceMapper(this.parse);
  private readonly organizationMapper = new ParseOrganizationMapper(this.parse);

  public async execute(command: ListDevices): Promise<ListedDevice[]> {
    const { organizationId, limit, searchTerm, olderThan } = command.details;

    const query = new this.parse.Query(DeviceTopology.TABLE);
    if (organizationId) {
      query.equalTo(
        DeviceTopology.ORGANIZATION,
        this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId)
      );
    }

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(DeviceTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    query.include(DeviceTopology.ORGANIZATION).include(DeviceTopology.TYPE).descending(ObjectTopology.CREATED_AT);

    if (limit) query.limit(limit);
    if (olderThan) query.lessThan(ObjectTopology.CREATED_AT, olderThan);

    const devices = await query.find(this.options);
    return devices.map((device) => {
      const domainDevice = this.deviceMapper.toDomain(device);
      const organization = device.get(DeviceTopology.ORGANIZATION);
      const organizationDomain = organization ? this.organizationMapper.toDomain(organization, 0) : undefined;
      return {
        id: domainDevice.id,
        name: domainDevice.name,
        organizationName: organizationDomain?.name ?? '',
        organizationLogoUrl: organizationDomain?.logoUrl,
        deviceType: domainDevice.deviceType
          ? {
              id: domainDevice.deviceType.id,
              name: domainDevice.deviceType.name,
              version: domainDevice.deviceType.version,
            }
          : undefined,
        serialNumber: domainDevice.serialNumber,
        createdAt: domainDevice.createdAt,
      };
    });
  }
}
